// Portfolio Administration v1.0 PR 4a §7.6 — Snooze a calendar item.
// Backend logs the snooze separately so >2 snoozes raise an E2 ticket
// (wired in PR 2c). This is just the UI surface.

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button, Textarea } from "@/design-system/components/ui";
import { addCalendarDays, isoDateInTz } from "@/design-system/lib/format";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useSnoozeCalendarItem } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import type { ComplianceCalendarItemDto } from "@/api/portfolioAdmin/types";

interface SnoozeItemModalProps {
  loanId: string;
  item: ComplianceCalendarItemDto;
  open: boolean;
  onClose: () => void;
}

export function SnoozeItemModal({
  loanId,
  item,
  open,
  onClose,
}: SnoozeItemModalProps) {
  const toast = useToast();
  const snooze = useSnoozeCalendarItem(loanId);

  // 🔴 A WEEK IS ADDED IN UTC, NOT LOCALLY. `item.dueDate` is a wall-calendar
  // value stored at UTC midnight (`ComplianceCalendarService` widens a
  // `DateOnly` with `TimeOnly.MinValue`), and the endpoint binds this string
  // straight back to a `DateOnly`. `setDate` is a LOCAL mutator, so where the
  // seven days cross a DST transition the result serialises a day early: a
  // Europe/London or Europe/Berlin browser turned a due date of `2026-03-25`
  // into `2026-03-31` where `2026-04-01` is correct — across a MONTH boundary.
  // Australia/Sydney does the same at its own October transition. No-DST zones
  // such as America/Phoenix never saw it, which is why it read as harmless.
  const defaultDate = isoDateInTz(addCalendarDays(item.dueDate, 7), "UTC");
  const [newDueDate, setNewDueDate] = useState(defaultDate);
  const [reason, setReason] = useState("");

  const onSubmit = async () => {
    if (!reason.trim()) {
      toast.error("A reason is required to snooze.");
      return;
    }
    try {
      await snooze.mutateAsync({
        itemId: item.id,
        body: { newDueDate, reason },
      });
      toast.success("Item snoozed.");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not snooze.",
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Snooze due-date"
      description={item.title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={snooze.isPending || !reason.trim()}
            data-testid="due-dates-snooze-submit"
          >
            Snooze
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="due-dates-snooze-form">
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">New due date</span>
          <input
            type="date"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={newDueDate}
            onChange={(e) => setNewDueDate(e.target.value)}
            data-testid="due-dates-snooze-date"
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Reason</span>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this being snoozed? (Repeated snoozes raise an E2 ticket.)"
            data-testid="due-dates-snooze-reason"
          />
        </label>
      </div>
    </Modal>
  );
}
